import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthReady } from "@provider/LineLoginProvider";
import { captureFamilyInviteFromUrl, clearFamilyInviteCode, getFamilyInviteCode } from "@utils/familyInvite";
import { getPayReturnTo, clearPayReturnTo } from "@utils/payReturnTo";
import { useCusAuth } from "@utils/store/auth/useCusAuth";

import StepChoose from "./components/addAccount/StepChoose";
import StepAddress from "./components/addAccount/StepAddress";
import StepMatching from "./components/addAccount/StepMatching";
import StepSelect from "./components/addAccount/StepSelect";
import JoinCode from "./components/addAccount/JoinCode";
import JoinRegister from "./components/addAccount/JoinRegister";
import JoinSerial from "./components/addAccount/JoinSerial";
import JoinError from "./components/addAccount/JoinError";
import JoinSuccess from "./components/addAccount/JoinSuccess";

/** 從 QR / 分享連結帶入的邀請碼淨化成後端字元集（A-Z2-9，最多 6 碼）。 */
const sanitizeInviteCode = (raw: string | null): string =>
  (raw ?? "").toUpperCase().replace(/[^A-Z2-9]/g, "").slice(0, 6);

/** 從瓦斯行序號連結帶入的客戶序號淨化：純數字，最多 6 碼（同 JoinSerial／InvitedCode 的 OTPInputBlocks 規則）。 */
const sanitizeSerial = (raw: string | null): string =>
  (raw ?? "").replace(/[^0-9]/g, "").slice(0, 6);

export default function AddAccountScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { cusJwtToken } = useCusAuth();

  // QR / 分享連結：?invite_code=ABC234 → 直接進入「加入既有的瓦斯帳戶」並預填。
  // 一次性消費：讀進 state 後立刻把 invite_code 從 URL 移除，否則 query 會殘留整個
  // addAccount 生命週期，導致「接受邀請後返回再進入」時又被預填同一組已用過的邀請碼。
  // URL 優先；LINE login round-trip 可能把 query 洗掉，此時退回進站時
  // captureFamilyInviteFromUrl 存進 sessionStorage 的邀請碼。
  const [prefillCode, setPrefillCode] = useState(
    () => sanitizeInviteCode(searchParams.get("invite_code")) || getFamilyInviteCode() || "",
  );

  // 瓦斯行序號連結：?serial=123456 → 直接進入「輸入客戶序號」並預填（僅預填，仍要按確認送出）。
  // 只認本次 URL 帶的值——這裡是已註冊者專用入口（見 LineLoginProvider 的
  // resolveSerialLinkRegisteredRedirect），不像家庭邀請碼需要撐過未登入的 LINE
  // login round-trip，故不比照 getFamilyInviteCode() 疊加 sessionStorage/Cookie 來源。
  const [prefillSerial, setPrefillSerial] = useState(() => sanitizeSerial(searchParams.get("serial")));

  // 讀完 URL 上的邀請碼／序號即移除，避免返回 / 重新整理時又自動帶入。
  //
  // 注意順序：React effect 是「子元件先跑、父（LineLoginProvider）後跑」——
  // 若這裡直接把 query 刪掉，provider 的 captureFamilyInviteFromUrl 會讀到
  // 已清空的 URL，sessionStorage 永遠存不到碼，LINE login round-trip 回來就撲空。
  // 因此刪 query 前先自己 capture 一次，保證碼活過 round-trip。
  useEffect(() => {
    if (!searchParams.has("invite_code") && !searchParams.has("serial")) return;
    if (searchParams.has("invite_code")) captureFamilyInviteFromUrl();
    const next = new URLSearchParams(searchParams);
    next.delete("invite_code");
    next.delete("serial");
    setSearchParams(next, { replace: true });
  }, []); // 僅在掛載時消費一次

  // screen: choose | address | matching | select | join_code | join_register | join_serial | join_error | join_success
  const [screen, setScreen] = useState(prefillSerial ? "join_serial" : prefillCode ? "join_code" : "choose");
  // 未註冊者頁內註冊（JoinRegister）用的邀請碼（手動輸入路徑由 JoinCode 帶過來）。
  const [regCode, setRegCode] = useState("");
  const authReady = useAuthReady();

  // 未註冊者（auth 完成後仍無 jwt）＋深連結預填 → 直接進頁內「註冊＋加入」流程，
  // 整段停留在 addAccount，不導去任何 /register 頁。
  useEffect(() => {
    if (authReady && !cusJwtToken && prefillCode && screen === "join_code") {
      setRegCode(prefillCode);
      setScreen("join_register");
    }
  }, [authReady, cusJwtToken, prefillCode, screen]);
  const [addr, setAddr] = useState("");
  const [errType, setErrType] = useState<string | null>(null);
  const [errCode, setErrCode] = useState("");
  const [info, setInfo] = useState<Record<string, unknown> | null>(null);

  //-- 當切換頁面時，觸發全局捲動至最上方事件
  useEffect(() => {
    const triggerScroll = () => {
      window.dispatchEvent(new Event("scroll-to-top"));
    };

    triggerScroll();
    // 雙重保障：給予渲染一些緩衝時間
    const timer = setTimeout(triggerScroll, 50);
    return () => clearTimeout(timer);
  }, [screen]);

  return (
    <>
      {screen === "choose" && (
        <StepChoose
          onNew={() => setScreen("address")}
          onJoin={() => setScreen("join_code")}
          onAddExisting={() => setScreen("join_serial")}
          onBack={() => navigate(-1)}
        />
      )}
      {screen === "address" && <StepAddress addr={addr} setAddr={setAddr} onBack={() => setScreen("choose")} onNext={() => setScreen("matching")} />}
      {screen === "matching" && <StepMatching addr={addr} onDone={() => setScreen("select")} onBack={() => setScreen("address")} />}
      {screen === "select" && <StepSelect onBack={() => setScreen("address")} />}
      {screen === "join_code" && (
        <JoinCode
          initialCode={prefillCode}
          onBack={() => {
            setPrefillCode("");
            setScreen("choose");
          }}
          onSuccess={(acc) => {
            clearFamilyInviteCode();
            setPrefillCode("");
            setInfo(acc);
            setScreen("join_success");
          }}
          onError={(type, code) => {
            setErrType(type);
            setErrCode(code || "");
            setScreen("join_error");
          }}
          onNeedRegister={(code) => {
            setRegCode(code);
            setScreen("join_register");
          }}
        />
      )}
      {screen === "join_register" && (
        <JoinRegister
          code={regCode || prefillCode}
          onBack={() => setScreen("join_code")}
          onSuccess={(acc) => {
            clearFamilyInviteCode();
            setPrefillCode("");
            setInfo(acc);
            setScreen("join_success");
          }}
          onError={(type, code) => {
            setErrType(type);
            setErrCode(code || "");
            setScreen("join_error");
          }}
        />
      )}
      {screen === "join_serial" && (
        <JoinSerial
          initialSerial={prefillSerial}
          onBack={() => {
            setPrefillSerial("");
            setScreen("choose");
          }}
          onSuccess={(acc: Record<string, unknown>) => {
            setPrefillSerial("");
            setInfo(acc);
            setScreen("join_success");
          }}
        />
      )}
      {screen === "join_error" && (
        <JoinError errorType={errType} code={errCode} onRetry={() => setScreen("join_code")} onCancel={() => setScreen("choose")} />
      )}
      {screen === "join_success" && info && (
        <JoinSuccess
          info={info}
          onDone={() => {
            // 掃付款 QR 進來的未註冊客戶：註冊+加入家庭完成後導回付款頁繼續付款；
            // 用 location.href 整頁重載，確保帶著新註冊的身分/JWT 進付款頁。
            const back = getPayReturnTo();
            if (back) {
              clearPayReturnTo();
              window.location.href = back;
              return;
            }
            navigate("/home");
          }}
        />
      )}
    </>
  );
}
